import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ApiUser } from '@allbin/mobilix-api-client';

import { reviveMetadata } from '../helpers/revivers';
import { useApiClient } from './useApiClient';
import { useUserLookupStore } from '../store/userLookup';

// The API returns this placeholder row for ids unknown to AllAuth —
// notably historical `auth0|…` actor ids that predate the migration.
const DELETED_USER_PLACEHOLDER = '<Deleted user>';

const messages = defineMessages({
  deletedUser: {
    id: 'user_lookup.deleted_user',
    defaultMessage: 'Deleted user',
  },
});

export const reviveUser: ReviverFn<ApiUser, User> = (user) => ({
  ...user,
  meta: reviveMetadata(user.meta),
});

interface IUseUserLookup {
  userLookup: (id: string) => User;
}

const userIdsToFetch = new Set<string>();
let timer: ReturnType<typeof setTimeout> | undefined = undefined;

export const useUserLookup = (): IUseUserLookup => {
  const intl = useIntl();
  const apiClient = useApiClient();
  const { users, mergeUsers } = useUserLookupStore();

  const fetchUser = React.useCallback(
    (id: string) => {
      userIdsToFetch.add(id);

      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }

      timer = setTimeout(() => {
        if (userIdsToFetch.size === 0) {
          return;
        }
        const req = apiClient.users.list(Array.from(userIdsToFetch));
        userIdsToFetch.clear();

        void req
          .then((users) => {
            mergeUsers(users.map((u) => reviveUser(u)));
          })
          .catch((err) => {
            console.error(err);
          });
      }, 300);
    },
    [apiClient, mergeUsers],
  );

  const userLookup = React.useCallback(
    (id: string) => {
      if (!users[id]) {
        fetchUser(id);
        return {
          id,
          name: `...`,
          user_id: id,
          email: 'loading@example.com',
        } as User;
      }
      const user = users[id];
      if (user.name === DELETED_USER_PLACEHOLDER) {
        return { ...user, name: intl.formatMessage(messages.deletedUser) };
      }
      return user;
    },
    [users, fetchUser, intl],
  );

  return {
    userLookup,
  };
};
